// Design-system style guide (route: /design). The living reference for the
// type system (Poppins scale, weights, specimen) and color (brand mark,
// semantic error/warning/success/info, full + chart palettes) — everything read
// live from the theme so it can't drift. Inherits the app's real color scheme
// (Mantine scopes its scheme CSS vars to :root, so a nested scheme "island"
// can't flip the surface) — use the sun/moon toggle in the header to compare
// light vs dark.
import {
    Alert,
    Badge,
    Button,
    Card,
    Code,
    Divider,
    Group,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    Title,
    useMantineTheme,
} from '@mantine/core'
import type { MantineSize } from '@mantine/core'
import type { ReactNode } from 'react'
import { IconAlertTriangle, IconPlus, IconSparkles } from '@tabler/icons-react'
import { CtaButton } from '@/components/CtaButton'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'

const SEMANTIC = ['error', 'warning', 'success', 'info'] as const
const REFERENCE = ['sage', 'plum'] as const
const TOKENS = [...SEMANTIC, ...REFERENCE]
const LIME = '#CCFF00' // abovedata.io brand

const labelOn = (hex: string) => {
    const [r, g, b] = [0, 2, 4].map(
        (i) => parseInt(hex.slice(1 + i, 3 + i), 16) / 255
    )
    const l = (c: number) =>
        c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
    return 0.2126 * l(r) + 0.7152 * l(g) + 0.0722 * l(b) > 0.4
        ? '#1E1C18'
        : '#FFFFFF'
}

// Dedicated categorical hues (coral · gold · emerald · magenta) on the same
// OKLCH ladder. The full chart sequence (CategoricalSection) interleaves the
// reused brand sage + plum and is ORDERED FOR COLORBLIND SAFETY: sage / coral /
// plum / gold first (CVD-distinct); emerald / magenta last (they collapse with
// sage/plum under CVD — use only with non-color cues). Semantics excluded.
const CATEGORICAL: Record<string, string[]> = {
    coral: [
        '#FFF0EB',
        '#FFDED4',
        '#FFC0AC',
        '#FF9B7C',
        '#ED7D5A',
        '#D1623E',
        '#B34C2B',
        '#91381B',
        '#6E270E',
        '#4F1806',
    ],
    gold: [
        '#F8F4E2',
        '#F1E7C0',
        '#E2D192',
        '#D0B85D',
        '#BBA02B',
        '#A08600',
        '#856E00',
        '#695700',
        '#4E4000',
        '#362C00',
    ],
    emerald: [
        '#E6F9EF',
        '#C8F2DC',
        '#9EE3C1',
        '#6CD0A4',
        '#3DBB89',
        '#00A070',
        '#00855C',
        '#006948',
        '#004E34',
        '#003623',
    ],
    magenta: [
        '#FFEEF8',
        '#FFDAF0',
        '#FCB9E2',
        '#F197D0',
        '#DE7ABB',
        '#C35EA1',
        '#A64987',
        '#85366C',
        '#652551',
        '#481739',
    ],
}

// Current theme palette (read live from the theme), shown the same way.
const CURRENT = [
    'sage',
    'plum',
    'error',
    'warning',
    'success',
    'info',
    'lime',
    'gray',
    'dark',
]

// Cooler dark neutrals kept defined in the theme but NOT live — the live `dark`
// is now a warm gray. Shown here (read from the theme) for reference / quick
// revert to a cool dark.
const DARK_ALTERNATES: { key: string; label: string; note: string }[] = [
    {
        key: 'coolSlate',
        label: 'cool slate',
        note: 'previous live dark · cool blue-gray (h~246)',
    },
    {
        key: 'navy',
        label: 'navy',
        note: 'site-matched deeper blue alternate (h~255)',
    },
]

// The Above Data caret mark (from abovedata.io) + wordmark, recolorable.
function Logo({
    caret,
    word,
    sub,
}: {
    caret: string
    word: string
    sub: string
}) {
    return (
        <Group gap={9} align='center' wrap='nowrap'>
            <svg
                width={26}
                height={29}
                viewBox='0 0 40 44'
                fill='none'
                aria-label='Above Data'
            >
                <path
                    d='M20 3 L38 41 M20 3 L2 41'
                    stroke={caret}
                    strokeWidth={5}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </svg>
            <Text fw={800} style={{ fontSize: 19, lineHeight: 1 }}>
                <span style={{ color: word }}>Above</span>{' '}
                <span style={{ color: sub }}>Data</span>
            </Text>
        </Group>
    )
}

function BrandSection() {
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Brand logo</Text>
            <Text c='dimmed' size='sm' mb='md'>
                lime #CCFF00 is the identity color — used on dark surfaces only.
                On light parchment the mark renders in the sage/teal primary
                instead (lime never touches parchment).
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                <Stack
                    gap={6}
                    p='lg'
                    align='center'
                    style={{
                        background: '#0A1220',
                        borderRadius: 'var(--mantine-radius-md)',
                    }}
                >
                    <Logo caret={LIME} word='#FFFFFF' sub='#94A3B8' />
                    <Text size='xs' style={{ color: '#94A3B8' }}>
                        on dark — lime caret
                    </Text>
                </Stack>
                <Stack
                    gap={6}
                    p='lg'
                    align='center'
                    style={{
                        background: '#F4F1EB',
                        borderRadius: 'var(--mantine-radius-md)',
                        border: '1px solid #DDD9CF',
                    }}
                >
                    <Logo caret='#15817A' word='#1E1C18' sub='#6E6A5F' />
                    <Text size='xs' style={{ color: '#6E6A5F' }}>
                        on light — sage caret (no lime)
                    </Text>
                </Stack>
            </SimpleGrid>
        </Paper>
    )
}

function ShadeStrip({
    name,
    shades,
}: {
    name: string
    shades: readonly string[]
}) {
    return (
        <div>
            <Text fw={600} size='sm' tt='capitalize' mb={4}>
                {name}
            </Text>
            <Group gap={0} wrap='nowrap'>
                {shades.map((hex, i) => (
                    <div
                        key={hex}
                        style={{
                            flex: 1,
                            height: 52,
                            background: hex,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1.25,
                        }}
                    >
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: labelOn(hex),
                            }}
                        >
                            {i}
                        </span>
                        <span
                            style={{
                                fontSize: 8,
                                color: labelOn(hex),
                                opacity: 0.85,
                            }}
                        >
                            {hex.replace('#', '')}
                        </span>
                    </div>
                ))}
            </Group>
        </div>
    )
}

function CurrentPaletteSection() {
    const theme = useMantineTheme()
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Current palette</Text>
            <Text c='dimmed' size='sm' mb='md'>
                The live theme scales — primary (sage/teal), accent (plum),
                semantic (error/warning/success/info), brand (lime), and
                neutrals (gray, dark taupe).
            </Text>
            <Stack gap='sm'>
                {CURRENT.map((n) => (
                    <ShadeStrip
                        key={n}
                        name={n}
                        shades={theme.colors[n] ?? []}
                    />
                ))}
            </Stack>
        </Paper>
    )
}

function DarkOptionsSection() {
    const theme = useMantineTheme()
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Dark neutral — alternates</Text>
            <Text c='dimmed' size='sm' mb='md'>
                The live dark is now a deep warm gray (see `dark` in the current
                palette above) — same OKLCH construction as the rest of the
                system at the same hue as the light parchment, on a deepened
                (darker) profile and kept very low-chroma so it reads warm, not
                brown. These cooler darks stay defined in the theme for
                reference / quick revert: cool slate was the previous live dark;
                navy is a deeper blue alternate.
            </Text>
            <Stack gap='sm'>
                {DARK_ALTERNATES.map(({ key, label, note }) => (
                    <div key={key}>
                        <ShadeStrip
                            name={label}
                            shades={theme.colors[key] ?? []}
                        />
                        <Text c='dimmed' size='xs' mt={2}>
                            {note}
                        </Text>
                    </div>
                ))}
            </Stack>
        </Paper>
    )
}

function CategoricalSection() {
    const theme = useMantineTheme()
    // Ordered for colorblind safety: the first four stay distinct under CVD.
    // sage/plum are reused brand colors (non-semantic); coral/gold are dedicated.
    const safe = [
        { name: 'sage (brand)', shades: theme.colors['sage'] ?? [] },
        { name: 'coral', shades: CATEGORICAL.coral },
        { name: 'plum (accent)', shades: theme.colors['plum'] ?? [] },
        { name: 'gold', shades: CATEGORICAL.gold },
    ]
    const extra = [
        { name: 'emerald', shades: CATEGORICAL.emerald },
        { name: 'magenta', shades: CATEGORICAL.magenta },
    ]
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Categorical / chart palette</Text>
            <Text c='dimmed' size='sm' mb='md'>
                Assign chart series in this order. The first four — sage, coral,
                plum, gold — stay distinct under red-green and blue-yellow
                colorblindness. emerald and magenta are added for variety but
                collapse with sage/plum under CVD, so use them only as a 5th/6th
                series AND always with a non-color cue (direct label, marker, or
                dash). Charts must never rely on color alone. Brand sage + plum
                are reused (non-semantic); error/warning/success/info are
                excluded so a series is never read as a status.
            </Text>
            <Text size='xs' fw={700} c='dimmed' mb={6}>
                CVD-SAFE — USE FIRST
            </Text>
            <Stack gap='sm'>
                {safe.map(({ name, shades }) => (
                    <ShadeStrip key={name} name={name} shades={shades} />
                ))}
            </Stack>
            <Text size='xs' fw={700} c='dimmed' mt='md' mb={6}>
                EXTRA — ONLY WITH A NON-COLOR CUE
            </Text>
            <Stack gap='sm'>
                {extra.map(({ name, shades }) => (
                    <ShadeStrip key={name} name={name} shades={shades} />
                ))}
            </Stack>
            <Divider my='md' />
            <Text size='xs' fw={700} c='dimmed' mb={6}>
                BUILDING A MULTI-SERIES CHART
            </Text>
            <Text c='dimmed' size='sm'>
                Assign colors with <Code>chartColor(i)</Code> in sequence, and —
                because color alone fails under colorblindness and b/w print —
                pair every series with a non-color cue: for line/area charts use{' '}
                <Code>chartSeriesStyle(i)</Code> (it adds a dash pattern + point
                marker); for bar charts use direct labels or fill patterns.
                Prefer to keep a chart to the first four colors. For ordered /
                quantitative data (one measure over time or buckets) do NOT use
                this categorical set — use a single color&apos;s 10-shade ramp
                instead. Resolve a color name to a value before Chart.js (canvas
                cannot read CSS variables).
            </Text>
        </Paper>
    )
}

// ---- Typography (the type system, read live from the theme so the guide can
// never drift from the real tokens) -----------------------------------------

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const
const HEADING_USE: Record<number, string> = {
    1: 'Page title — one per screen',
    2: 'Section title',
    3: 'Subsection / card group',
    4: 'Card title',
    5: 'Inline group label',
    6: 'Smallest heading / overline',
}

const BODY_SIZES: { size: MantineSize; use: string }[] = [
    { size: 'xl', use: 'Section intro / lead-in' },
    { size: 'lg', use: 'Lead paragraph, emphasis' },
    { size: 'md', use: 'Body default' },
    { size: 'sm', use: 'Secondary / dense UI text' },
    { size: 'xs', use: 'Micro labels, badges, metadata' },
]

const WEIGHTS: { weight: number; name: string; use: string }[] = [
    { weight: 400, name: 'Regular', use: 'Body and secondary text' },
    { weight: 500, name: 'Medium', use: 'Emphasis, labels, navigation' },
    { weight: 600, name: 'SemiBold', use: 'Headings and titles' },
    {
        weight: 700,
        name: 'Bold',
        use: 'Big display numbers ("weight moments")',
    },
]

function HeadingsBlock() {
    const theme = useMantineTheme()
    return (
        <Stack gap='sm'>
            {HEADING_LEVELS.map((level) => {
                const spec = theme.headings.sizes?.[`h${level}` as 'h1']
                return (
                    <Group
                        key={level}
                        justify='space-between'
                        align='baseline'
                        wrap='nowrap'
                        gap='md'
                    >
                        <Title order={level} style={{ minWidth: 0 }}>
                            {HEADING_USE[level]}
                        </Title>
                        <Text
                            c='dimmed'
                            size='xs'
                            ff='monospace'
                            ta='right'
                            style={{ flexShrink: 0 }}
                        >
                            h{level} · {String(spec?.fontSize)} /{' '}
                            {String(spec?.lineHeight)} ·{' '}
                            {String(spec?.fontWeight)}
                        </Text>
                    </Group>
                )
            })}
        </Stack>
    )
}

function BodyScaleBlock() {
    const theme = useMantineTheme()
    return (
        <Stack gap='sm'>
            {BODY_SIZES.map(({ size, use }) => (
                <Group
                    key={size}
                    justify='space-between'
                    align='baseline'
                    wrap='nowrap'
                    gap='md'
                >
                    <Text size={size} style={{ minWidth: 0 }}>
                        The quick brown fox — {use}
                    </Text>
                    <Text
                        c='dimmed'
                        size='xs'
                        ff='monospace'
                        ta='right'
                        style={{ flexShrink: 0 }}
                    >
                        {size} · {String(theme.fontSizes[size])} /{' '}
                        {String(theme.lineHeights[size])}
                    </Text>
                </Group>
            ))}
        </Stack>
    )
}

function WeightsBlock() {
    return (
        <Stack gap='sm'>
            {WEIGHTS.map(({ weight, name, use }) => (
                <Group
                    key={weight}
                    justify='space-between'
                    align='baseline'
                    wrap='nowrap'
                    gap='md'
                >
                    <Text fw={weight} size='lg' style={{ minWidth: 0 }}>
                        {name} — Above Data audiences
                    </Text>
                    <Text
                        c='dimmed'
                        size='xs'
                        ff='monospace'
                        ta='right'
                        style={{ flexShrink: 0 }}
                    >
                        {weight} · {use}
                    </Text>
                </Group>
            ))}
            <Text c='dimmed' size='xs'>
                Weight 300 (Light) is intentionally NOT loaded: the brief
                proposed it for secondary text, but thin weights read poorly at
                UI sizes, so secondary text uses weight 400 with a dimmed color
                instead. Legibility (web best practice) wins over the brief.
            </Text>
        </Stack>
    )
}

// A realistic content block — shows the type system in the register it targets:
// warm, considered, human (the "Notion / Superhuman" feel), not generic-AI.
function SpecimenBlock() {
    return (
        <Paper withBorder p='lg' radius='md'>
            <Group justify='space-between' align='flex-start' wrap='nowrap'>
                <Stack gap={4} style={{ minWidth: 0 }}>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                        Audience
                    </Text>
                    <Title order={4}>High-intent grocery shoppers</Title>
                </Stack>
                <Stack gap={0} align='flex-end' style={{ flexShrink: 0 }}>
                    <Text
                        fz={28}
                        fw={700}
                        lh={1.1}
                        c='var(--mantine-primary-color-filled)'
                    >
                        ~2.4M
                    </Text>
                    <Text size='xs' c='dimmed' tt='uppercase' fw={600}>
                        Reach
                    </Text>
                </Stack>
            </Group>
            <Text size='sm' mt='sm'>
                Households that bought premium grocery brands in the last 90
                days, crossed with users who engaged with sustainability
                content. Built conversationally and refined in seconds.
            </Text>
            <Group gap={6} mt='sm'>
                <Badge variant='light' color='sage' size='sm'>
                    Transaction
                </Badge>
                <Badge variant='light' color='plum' size='sm'>
                    User attribute
                </Badge>
            </Group>
        </Paper>
    )
}

function TypographySection() {
    const theme = useMantineTheme()
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Typography</Text>
            <Text c='dimmed' size='sm' mb='md'>
                Poppins — a geometric, warm sans (the brand typeface),
                self-hosted via @fontsource (no Google Fonts hotlink; GDPR-clean
                for the EU launch). One stack drives Mantine body + headings AND
                the CopilotKit chat (bridged through --cpk-font-sans), so the
                whole app shares one voice: capable but approachable,
                considered, human.
            </Text>
            <Code block mb='md'>
                {theme.fontFamily}
            </Code>

            <Divider
                label='Headings — Poppins SemiBold (600)'
                labelPosition='left'
                mb='sm'
            />
            <HeadingsBlock />

            <Divider label='Body scale' labelPosition='left' my='md' />
            <BodyScaleBlock />

            <Divider label='Weights' labelPosition='left' my='md' />
            <WeightsBlock />

            <Divider label='In context' labelPosition='left' my='md' />
            <SpecimenBlock />
        </Paper>
    )
}

// ---- Buttons (the emphasis hierarchy) --------------------------------------

function ButtonTierRow({
    tier,
    usage,
    children,
}: {
    tier: string
    usage: string
    children: ReactNode
}) {
    return (
        <Group justify='space-between' align='center' wrap='nowrap' gap='lg'>
            <div style={{ flex: 1, minWidth: 0 }}>
                <Text size='sm' fw={600}>
                    {tier}
                </Text>
                <Text size='xs' c='dimmed'>
                    {usage}
                </Text>
            </div>
            <Group
                gap='xs'
                wrap='wrap'
                justify='flex-end'
                style={{ flexShrink: 0, maxWidth: '46%' }}
            >
                {children}
            </Group>
        </Group>
    )
}

function ButtonsSection() {
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Buttons</Text>
            <Text c='dimmed' size='sm' mb='md'>
                Five emphasis levels. The one hard rule: exactly ONE primary per
                view or modal — never two filled buttons competing. Match the
                variant to how important the action is; everything below is the
                live Mantine variant.
            </Text>
            <Stack gap='lg'>
                <ButtonTierRow
                    tier='Primary — filled (sage)'
                    usage='The single main action of a view or modal. CtaButton is just this with the variant preset (the marquee "New audience" CTA).'
                >
                    <Button>Save changes</Button>
                    <CtaButton leftSection={<IconPlus size={16} />}>
                        New audience
                    </CtaButton>
                </ButtonTierRow>
                <ButtonTierRow
                    tier='Secondary · accent — variant="light"'
                    usage='Inviting secondary / soft CTA (brand tint). Add a status COLOR when the tint should carry meaning (live, ready, needs-setup).'
                >
                    <Button variant='light'>Distribute to more</Button>
                    <Button variant='light' color='success'>
                        Live · 3
                    </Button>
                    <Button variant='light' color='warning'>
                        Finish setup
                    </Button>
                </ButtonTierRow>
                <ButtonTierRow
                    tier='Secondary · neutral — variant="default"'
                    usage='Neutral or dismissive secondary; the standard partner to a primary (modal Cancel, a Back button).'
                >
                    <Button variant='default'>Cancel</Button>
                </ButtonTierRow>
                <ButtonTierRow
                    tier='Tertiary — variant="subtle"'
                    usage='Quiet, low-emphasis actions: Details, View all, an in-page Back link.'
                >
                    <Button variant='subtle'>View all</Button>
                </ButtonTierRow>
                <ButtonTierRow
                    tier='Destructive — color="error"'
                    usage='Filled inside a confirm dialog (the DeleteButton popover); subtle/light at rest elsewhere.'
                >
                    <Button variant='subtle' color='error'>
                        Remove
                    </Button>
                    <Button color='error'>Delete</Button>
                </ButtonTierRow>
            </Stack>
            <Divider
                label='Modal footer pattern'
                labelPosition='left'
                my='md'
            />
            <Group justify='flex-end'>
                <Button variant='default'>Cancel</Button>
                <Button>Save connection</Button>
            </Group>
            <Text c='dimmed' size='xs' mt='xs'>
                Dismiss/Cancel on the left as the neutral secondary; the primary
                action on the right. A destructive action (if any) sits far left
                as a subtle red button.
            </Text>
        </Paper>
    )
}

// ---- Empty state (the shared "nothing here yet" block) --------------------

function EmptyStateSection() {
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Empty state</Text>
            <Text c='dimmed' size='sm' mb='md'>
                The shared `&lt;EmptyState&gt;` for empty screens (no data yet)
                — a bordered card with a tinted icon, title, description, and an
                optional CTA. Use it for no-data states; for a load FAILURE use
                an inline error card with a Retry instead (a different concern).
            </Text>
            <div style={{ display: 'flex', minHeight: 240 }}>
                <EmptyState
                    color='sage'
                    icon={<IconSparkles size={28} />}
                    title='Nothing here yet'
                    description='Start a conversation with the agent to ideate a strategy and build your first audience.'
                    action={
                        <CtaButton leftSection={<IconPlus size={16} />} mt='xs'>
                            New audience
                        </CtaButton>
                    }
                />
            </div>
        </Paper>
    )
}

// ---- Attention banner (the shared needs-attention notice) ------------------

function AttentionBannerSection() {
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Attention banner</Text>
            <Text c='dimmed' size='sm' mb='md'>
                The one warning notice — a warning-tinted Alert (
                <Code>AttentionBanner</Code>) used for every needs-attention
                message: the page-top banners AND in-context warnings inside
                modals/forms, so they all read the same. Title is required; pass
                children for a detail list, and override the icon only for a
                distinct cue. For errors/info use a plain Alert with the
                matching color.
            </Text>
            <AttentionBanner title='2 things need your attention'>
                <Text size='sm'>
                    A short, plain next step goes here — what is wrong and how
                    to fix it.
                </Text>
            </AttentionBanner>
        </Paper>
    )
}

// ---- Surfaces (canvas vs elevated card) ------------------------------------

function SurfacesSection() {
    return (
        <Paper withBorder p='md'>
            <Text fw={600}>Surfaces</Text>
            <Text c='dimmed' size='sm' mb='md'>
                Two neutral surfaces. The CANVAS (
                <Code>--mantine-color-body</Code>) is the page background; CARDS
                sit on it as a lifted surface (
                <Code>--mantine-color-default</Code>) with a hairline border —
                lifting in both light and dark. Use <Code>Card</Code> for every
                content card so the fill stays consistent; reserve{' '}
                <Code>Paper</Code> for flush insets that intentionally share the
                canvas.
            </Text>
            <div
                style={{
                    background: 'var(--mantine-color-body)',
                    border: '1px dashed var(--mantine-color-default-border)',
                    borderRadius: 'var(--mantine-radius-md)',
                    padding: 'var(--mantine-spacing-xl)',
                }}
            >
                <Text size='xs' c='dimmed' mb='sm'>
                    Canvas — page background
                </Text>
                <Card withBorder>
                    <Text size='sm' fw={600}>
                        Card — elevated surface
                    </Text>
                    <Text size='sm' c='dimmed'>
                        Fill + hairline border + a subtle shadow, lifting off
                        the canvas in both schemes.
                    </Text>
                </Card>
            </div>
        </Paper>
    )
}

function TokenRow({ token }: { token: string }) {
    return (
        <Paper withBorder p='sm'>
            <Group justify='space-between' wrap='nowrap' align='flex-start'>
                <Text fw={600} w={84} tt='capitalize' style={{ flexShrink: 0 }}>
                    {token}
                </Text>
                <Group gap='xs' wrap='wrap' justify='flex-end'>
                    <Badge color={token} variant='filled'>
                        filled
                    </Badge>
                    <Badge color={token} variant='light'>
                        light
                    </Badge>
                    <Button color={token} size='compact-sm'>
                        filled
                    </Button>
                    <Button color={token} variant='light' size='compact-sm'>
                        light
                    </Button>
                    <ThemeIcon color={token} variant='light'>
                        <IconAlertTriangle size={16} />
                    </ThemeIcon>
                    <Text c={token} fw={600}>
                        text
                    </Text>
                </Group>
            </Group>
            <Alert
                color={token}
                title={`${token} alert`}
                icon={<IconAlertTriangle size={16} />}
                mt='sm'
            >
                Example {token} alert body.
            </Alert>
        </Paper>
    )
}

export function DesignPage() {
    return (
        <Stack p='md'>
            <div>
                <Title order={1}>Design system — style guide</Title>
                <Text c='dimmed' size='sm'>
                    The living reference for type and color. Typography, the
                    brand mark, semantic colors (error / warning / success /
                    info), the full palette, and the chart palette — all read
                    live from the theme. Use the sun/moon toggle in the header
                    to compare light vs dark.
                </Text>
            </div>
            <TypographySection />
            <ButtonsSection />
            <EmptyStateSection />
            <AttentionBannerSection />
            <SurfacesSection />
            <BrandSection />
            <div>
                <Title order={2}>Color</Title>
                <Text c='dimmed' size='sm'>
                    Semantic colors across the real Mantine components they are
                    used in, with brand / secondary for reference.
                </Text>
            </div>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing='sm'>
                {TOKENS.map((t) => (
                    <TokenRow key={t} token={t} />
                ))}
            </SimpleGrid>
            <CurrentPaletteSection />
            <DarkOptionsSection />
            <CategoricalSection />
        </Stack>
    )
}
